import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Book,
  ChevronRight,
  ArrowLeft,
  ExternalLink,
  Video,
  Tag,
  Clock,
  Rocket,
  Lock,
  LayoutDashboard,
  GitBranch,
  Users,
  CheckSquare,
  Package,
  AlertTriangle,
  Copy,
  MessageCircle,
  BarChart2,
  UserCog,
  Target,
  IndianRupee,
  Settings,
  HelpCircle,
  FileText,
} from 'lucide-react';
import {
  useDocumentationCategories,
  useDocumentationArticles,
  useDocumentationArticle,
  useDocumentationSearch,
} from '../hooks/useDocumentation';
import { DocumentationArticleListItem, DocumentationSearchResult, getCategoryConfig, CATEGORY_CONFIGS } from '../types/documentation';

// Icon mapping for categories
const categoryIcons: Record<string, React.ReactNode> = {
  GETTING_STARTED: <Rocket className="h-5 w-5" />,
  AUTHENTICATION: <Lock className="h-5 w-5" />,
  DASHBOARD: <LayoutDashboard className="h-5 w-5" />,
  PIPELINE: <GitBranch className="h-5 w-5" />,
  LEADS: <Users className="h-5 w-5" />,
  TASKS: <CheckSquare className="h-5 w-5" />,
  DELIVERABLES: <Package className="h-5 w-5" />,
  RISK_ASSESSMENT: <AlertTriangle className="h-5 w-5" />,
  DUPLICATES: <Copy className="h-5 w-5" />,
  WHATSAPP: <MessageCircle className="h-5 w-5" />,
  ANALYTICS: <BarChart2 className="h-5 w-5" />,
  USER_MANAGEMENT: <UserCog className="h-5 w-5" />,
  TARGETS: <Target className="h-5 w-5" />,
  FINANCE: <IndianRupee className="h-5 w-5" />,
  SETTINGS: <Settings className="h-5 w-5" />,
  FAQ: <HelpCircle className="h-5 w-5" />,
};

// Simple Markdown renderer component
const MarkdownRenderer: React.FC<{ content: string }> = ({ content }) => {
  // Basic markdown parsing
  const parseMarkdown = (text: string): React.ReactNode[] => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeContent = '';
    let listItems: string[] = [];
    let listType: 'ul' | 'ol' | null = null;

    const flushList = () => {
      if (listItems.length > 0) {
        const ListTag = listType === 'ol' ? 'ol' : 'ul';
        elements.push(
          <ListTag key={elements.length} className={listType === 'ol' ? 'list-decimal list-inside space-y-1 my-2' : 'list-disc list-inside space-y-1 my-2'}>
            {listItems.map((item, i) => (
              <li key={i} className="text-gray-700">{item}</li>
            ))}
          </ListTag>
        );
        listItems = [];
        listType = null;
      }
    };

    lines.forEach((line, index) => {
      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={elements.length} className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto my-4 text-sm">
              <code>{codeContent.trim()}</code>
            </pre>
          );
          codeContent = '';
        }
        inCodeBlock = !inCodeBlock;
        flushList();
        return;
      }

      if (inCodeBlock) {
        codeContent += line + '\n';
        return;
      }

      // Headers
      if (line.startsWith('# ')) {
        flushList();
        elements.push(<h1 key={elements.length} className="text-3xl font-bold text-gray-900 mt-6 mb-4">{line.slice(2)}</h1>);
        return;
      }
      if (line.startsWith('## ')) {
        flushList();
        elements.push(<h2 key={elements.length} className="text-2xl font-semibold text-gray-800 mt-6 mb-3">{line.slice(3)}</h2>);
        return;
      }
      if (line.startsWith('### ')) {
        flushList();
        elements.push(<h3 key={elements.length} className="text-xl font-semibold text-gray-800 mt-4 mb-2">{line.slice(4)}</h3>);
        return;
      }
      if (line.startsWith('#### ')) {
        flushList();
        elements.push(<h4 key={elements.length} className="text-lg font-medium text-gray-800 mt-3 mb-2">{line.slice(5)}</h4>);
        return;
      }

      // Lists
      if (line.match(/^[-*] /)) {
        if (listType !== 'ul') flushList();
        listType = 'ul';
        listItems.push(line.slice(2));
        return;
      }
      if (line.match(/^\d+\. /)) {
        if (listType !== 'ol') flushList();
        listType = 'ol';
        listItems.push(line.replace(/^\d+\. /, ''));
        return;
      }

      // Bold and Q&A
      if (line.startsWith('**Q:') || line.startsWith('**Q ')) {
        flushList();
        const question = line.replace(/\*\*/g, '');
        elements.push(<p key={elements.length} className="font-semibold text-gray-900 mt-4">{question}</p>);
        return;
      }
      if (line.startsWith('A:') || line.startsWith('A ')) {
        elements.push(<p key={elements.length} className="text-gray-700 mb-4 pl-4">{line.slice(2)}</p>);
        return;
      }

      // Empty lines
      if (line.trim() === '') {
        flushList();
        return;
      }

      // Regular paragraph
      flushList();
      // Handle inline bold
      const formattedLine = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      elements.push(
        <p key={elements.length} className="text-gray-700 my-2" dangerouslySetInnerHTML={{ __html: formattedLine }} />
      );
    });

    flushList();
    return elements;
  };

  return <div className="prose max-w-none">{parseMarkdown(content)}</div>;
};

// Article card component
const ArticleCard: React.FC<{
  article: DocumentationArticleListItem | DocumentationSearchResult;
  onClick: () => void;
}> = ({ article, onClick }) => {
  const config = getCategoryConfig(article.category);

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="font-medium text-gray-900 group-hover:text-blue-600">{article.title}</h3>
          {article.summary && (
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{article.summary}</p>
          )}
          <div className="flex items-center gap-2 mt-3">
            <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
              {categoryIcons[article.category] || <FileText className="h-3 w-3" />}
              <span>{config.label}</span>
            </span>
            {article.videoUrl && (
              <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded">
                <Video className="h-3 w-3" />
                Video
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
      </div>
    </motion.div>
  );
};

// Category card component
const CategoryCard: React.FC<{
  category: string;
  count: number;
  label: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ category, count, label, isActive, onClick }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`w-full text-left p-3 rounded-lg transition-all ${
        isActive
          ? 'bg-blue-50 border-2 border-blue-500 text-blue-700'
          : 'bg-white border border-gray-200 hover:border-blue-300 hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
            {categoryIcons[category] || <FileText className="h-5 w-5" />}
          </div>
          <span className="font-medium">{label}</span>
        </div>
        <span className={`text-sm ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>{count}</span>
      </div>
    </motion.button>
  );
};

export const DocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(searchParams.get('category'));
  const [selectedArticleSlug, setSelectedArticleSlug] = useState<string | null>(searchParams.get('article'));

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Sync URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (selectedArticleSlug) params.set('article', selectedArticleSlug);
    setSearchParams(params, { replace: true });
  }, [searchQuery, selectedCategory, selectedArticleSlug, setSearchParams]);

  // Queries
  const { data: categories, isLoading: categoriesLoading } = useDocumentationCategories();
  const { data: articlesData, isLoading: articlesLoading } = useDocumentationArticles({
    category: selectedCategory || undefined,
    limit: 50,
  });
  const { data: searchResults, isLoading: searchLoading } = useDocumentationSearch(
    debouncedSearch,
    selectedCategory || undefined
  );
  const { data: selectedArticle, isLoading: articleLoading } = useDocumentationArticle(
    selectedArticleSlug || undefined
  );

  // Handlers
  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    setSelectedArticleSlug(null);
  };

  const handleArticleSelect = (slug: string) => {
    setSelectedArticleSlug(slug);
  };

  const handleBack = () => {
    setSelectedArticleSlug(null);
  };

  // Determine what articles to show
  const displayArticles = debouncedSearch.length >= 2 ? searchResults : articlesData?.articles;
  const isSearching = debouncedSearch.length >= 2;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Book className="h-8 w-8 text-blue-600" />
              Documentation
            </h1>
            <p className="text-gray-600 mt-1">
              Step-by-step guides and help for all features
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search documentation... (Ctrl + /)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-lg"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              Clear
            </button>
          )}
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Sidebar - Categories */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky top-6 space-y-2">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Categories
              </h2>

              {/* All Articles button */}
              <CategoryCard
                category="ALL"
                count={articlesData?.total || 0}
                label="All Articles"
                isActive={!selectedCategory}
                onClick={() => handleCategorySelect(null)}
              />

              {/* Category list */}
              {categoriesLoading ? (
                <div className="space-y-2">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : (
                categories?.map((cat) => (
                  <CategoryCard
                    key={cat.category}
                    category={cat.category}
                    count={cat.count}
                    label={cat.label}
                    isActive={selectedCategory === cat.category}
                    onClick={() => handleCategorySelect(cat.category)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              {/* Article View */}
              {selectedArticleSlug && selectedArticle ? (
                <motion.div
                  key="article"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white rounded-lg border border-gray-200 p-6"
                >
                  {/* Back button */}
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to articles
                  </button>

                  {/* Article header */}
                  <div className="border-b pb-4 mb-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                      <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                        {categoryIcons[selectedArticle.category]}
                        <span>{getCategoryConfig(selectedArticle.category).label}</span>
                      </span>
                      {selectedArticle.videoUrl && (
                        <a
                          href={selectedArticle.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 bg-purple-100 text-purple-600 px-2 py-1 rounded hover:bg-purple-200"
                        >
                          <Video className="h-3 w-3" />
                          Watch Video
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">{selectedArticle.title}</h1>
                    {selectedArticle.summary && (
                      <p className="text-gray-600 mt-2">{selectedArticle.summary}</p>
                    )}
                    {selectedArticle.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {selectedArticle.tags.map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded"
                          >
                            <Tag className="h-3 w-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-400 mt-3">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Updated {new Date(selectedArticle.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Article content */}
                  <div className="prose max-w-none">
                    <MarkdownRenderer content={selectedArticle.content} />
                  </div>

                  {/* Related articles */}
                  {selectedArticle.children && selectedArticle.children.length > 0 && (
                    <div className="mt-8 pt-6 border-t">
                      <h3 className="text-lg font-semibold mb-4">Related Articles</h3>
                      <div className="grid gap-3">
                        {selectedArticle.children.map((child) => (
                          <button
                            key={child.id}
                            onClick={() => handleArticleSelect(child.slug)}
                            className="text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                          >
                            <h4 className="font-medium text-gray-900">{child.title}</h4>
                            {child.summary && (
                              <p className="text-sm text-gray-500 mt-1">{child.summary}</p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : (
                /* Articles List */
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* Results header */}
                  <div className="mb-4">
                    {isSearching ? (
                      <h2 className="text-lg font-semibold text-gray-700">
                        {searchLoading ? 'Searching...' : `${searchResults?.length || 0} results for "${debouncedSearch}"`}
                      </h2>
                    ) : (
                      <h2 className="text-lg font-semibold text-gray-700">
                        {selectedCategory
                          ? `${getCategoryConfig(selectedCategory).label} (${displayArticles?.length || 0})`
                          : `All Articles (${articlesData?.total || 0})`}
                      </h2>
                    )}
                  </div>

                  {/* Articles grid */}
                  {articlesLoading || searchLoading ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="h-32 bg-gray-100 rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : displayArticles && displayArticles.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      {displayArticles.map((article) => (
                        <ArticleCard
                          key={article.id}
                          article={article}
                          onClick={() => handleArticleSelect(article.slug)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                      <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-700">No articles found</h3>
                      <p className="text-gray-500 mt-1">
                        {isSearching
                          ? 'Try a different search term'
                          : 'No documentation available for this category yet'}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
